<template>
  <b-overlay :show="loading" opacity="0.8">
    <request-diagnostics-modal
      :consultation-data="consultationData"
      :is-add-active.sync="toggleModal"
      :patient-data="patientData"
      resource="lab_requests"
      @refetchData="handleRequest"
    />

    <validation-observer ref="form_ref">
      <b-card v-if="consultationData" class="rounded-lg">
        <template #header>
          <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
            <h4 class="text-dark m-0">
              Diagnostics/Investigations
            </h4>

            <div class="d-flex align-items-center justify-content-start">
              <div v-if="can('lab_requests:CanAddNew')" class="d-flex align-items-center">
                <b-button size="sm" variant="link" class="p-0" @click="onAddNew">
                  <feather-icon
                    icon="PlusCircleIcon"
                    size="25"
                  />
                </b-button>
              </div>
            </div>
          </div>
        </template>

        <div class="no-card-body">
          <div v-if="getValueFromSource(consultationData, 'lab_requests', []) && getValueFromSource(consultationData, 'lab_requests', []).length">
            <div v-for="labRequest in getValueFromSource(consultationData, 'lab_requests', [])" :key="labRequest._id" class="mb-2">
              <div class="d-flex justify-content-between align-items-center bg-light-primary px-2 pt-1" style="border-top-left-radius: 10px; border-top-right-radius: 10px;">
                <div class="d-flex align-items-center">
                  <h6 v-b-toggle="`collapse-${labRequest._id}`" class="text-dark m-0">{{ getValueFromSource(labRequest, 'lab_category.title') }}</h6>
                  <b-link class="text-danger ml-2" @click="onCancel(labRequest)">
                    <feather-icon icon="XIcon" />
                    <span class="align-middle ml-50">Cancel</span>
                  </b-link>
                </div>
                
                <p v-b-toggle="`collapse-${labRequest._id}`" class="text-muted">
                  {{ formatDate(labRequest.created) }}
    
                  <span class="d-inline-block ml-2 when-closed">
                    <feather-icon icon="ChevronDownIcon" size="20" />
                  </span>
    
                  <span class="d-inline-block ml-2 when-open">
                    <feather-icon icon="ChevronUpIcon" size="20" />
                  </span>
                </p>
              </div>
    
              <b-collapse :id="`collapse-${labRequest._id}`" class="">
                <b-card class="" style="background-color: #F5FAFD; border-radius: 0;">
                  <div class="mb-2">
                    <label>Doctor Note</label>
                    <p class="card-text">{{ labRequest.doctor_note || '-' }}</p>
                  </div>
    
                  <div class="mb-2">
                    <label>Diagnostic Note</label>
                    <p class="card-text">{{ labRequest.lab_note || '-' }}</p>
                  </div>

                  <div class="mb-2">
                    <label>Lab Types ({{ getValueFromSource(labRequest, 'lab_types', []).length }})</label>
    
                    <div class="d-flex align-items-center flex-wrap">
                      <p 
                        v-for="labType in getValueFromSource(labRequest, 'lab_types', [])" 
                        :key="labType._id" 
                        class="d-inline-block mr-2"
                        target="_blank"
                      >
                        {{ labType.name }}
                      </p>
                    </div>
                  </div>
    
                  <div class="mb-2">
                    <label>Lab Reports ({{ getValueFromSource(labRequest, 'lab_uploads', []).length }})</label>
    
                    <div class="d-flex align-items-center flex-wrap">
                      <b-link 
                        v-for="labReport in getValueFromSource(labRequest, 'lab_uploads', [])" 
                        :key="labReport._id" 
                        :href="labReport.path"
                        class="d-inline-block mr-2"
                        target="_blank"
                      >
                        <b-img v-if="getExt(labReport.path) !== 'pdf'" :src="labReport.path" style="width: 100px; height: 100px;" />
                        <b-img v-else src="@/assets/images/download-pdf-icon.webp" style="width: 100px; height: 100px;" />
                      </b-link>
                    </div>
                  </div>
                </b-card>
              </b-collapse>
            </div>
          </div>
    
          <div v-else>
            <p class="lead">No records found.</p>
          </div>
        </div>
      </b-card>
    </validation-observer>
  </b-overlay>
</template>

<script>
import {
  BRow,
  BCol,
  BImg,
  BCard,
  BForm,
  BLink,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  VBToggle,
  BDropdown,
  BCollapse,
  BFormGroup,
  BDropdownItem,
  BFormTextarea,
  BFormInvalidFeedback,
} from 'bootstrap-vue'

import { get } from 'lodash';
import { formatDate } from '@/@core/utils/utils';
import { heightTransition } from '@core/mixins/ui/transition';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import RequestDiagnosticsModal from '@/@core/components/shared/admin/modals/RequestDiagnosticsModal.vue';


export default {
  components: {
    BRow,
    BCol,
    BImg,
    BCard,
    BForm,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BOverlay,
    BCollapse,
    BDropdown,
    BFormGroup,
    BFormTextarea,
    BDropdownItem,
    BFormInvalidFeedback,
    ListTable,
    BLink,
    
    RequestDiagnosticsModal,
    // validations
    ValidationProvider,
    ValidationObserver
  },
  directives: {
    'b-toggle': VBToggle,
  },
  mixins: [heightTransition],
  props: {
    consultationData: {
      type: Object,
      required: true,
    },
    patientData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      labRequestId: null,

      toggleModal: false,
      toggleUploadModal: false,

      records: [],
    }
  },
  computed: {
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  methods: {
    async onCancel(form_data){
      try {
        this.loading = true;

        await this.useJwt().mainService.labRequests.cancel(form_data._id);
        this.$emit('refresh')

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Request cancelled successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    getExt(link) {
      return link.split(/[#?]/)[0].split('.').pop().trim();
    },
    onViewUpload(lab_request){
      this.labRequestId = lab_request._id
      this.toggleUploadModal = true;
    },
    handleRequest(){
      this.$emit('refresh')
    },
    onAddNew(){
      this.toggleModal = true;
    },
    formatDate,
  }
}
</script>

<style scoped>
.profile-highlight {
  font-size: 18px !important;
}
.profile-highlight-title {
  font-size: 16px !important;
  color: lightgray;
  font-weight: normal;
}

.patient-name {
  font-size: 25px;
}

.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none !important;
}
</style>
